import { createContext } from 'react';

import type { AuthCredential, User, UserCredential } from 'firebase/auth';
import type { UploadTask } from 'firebase/storage';

export interface AuthInstanceInterface {
  confirmPasswordReset: (actionCode: string, newPassword: string) => Promise<void>;
  createUserWithEmailAndPassword: (email: string, password: string) => Promise<UserCredential>;
  getCurrentUser: () => User | null;
  getEmailVerified: () => boolean;
  getIdToken: () => Promise<string>;
  handleRecoverEmail: (actionCode: string, lang: string) => void;
  linkWithCredential: (credential: AuthCredential) => Promise<UserCredential> | null;
  linkWithEmailAndPassword: (email: string, password: string) => Promise<UserCredential>;
  linkWithGoogle: () => Promise<UserCredential> | null;
  linkWithTwitch: () => Promise<UserCredential> | null;
  linkWithDiscord: () => Promise<UserCredential> | null;
  onLogin: (callback: (idToken: string, anonymous: boolean) => void) => void;
  onError: (callback: (error: unknown) => void) => void;
  onLogout: (callback: () => void) => void;
  passwordReset: (email: string) => Promise<void>;
  refreshIdToken: () => Promise<void>;
  removeUserAvatar: (fileName: string) => Promise<void>;
  sendEmailVerification: () => Promise<void> | null;
  setEmailVerified: (emailVerified: boolean) => void;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithTwitch: () => Promise<UserCredential>;
  signInWithDiscord: () => Promise<UserCredential>;
  signInWithCustomToken: (token: string) => Promise<UserCredential>;
  signInAnonymously: () => Promise<UserCredential>;
  signOut: () => Promise<void>;
  unsubscribe: () => void;
  updatePassword: (newPassword: string) => Promise<void> | null;
  uploadUserAvatar: (file: File) => UploadTask;
  verifyBeforeUpdateEmail: (newEmail: string) => Promise<void> | null;
  verifyEmail: (actionCode: string) => Promise<void>;
  verifyPasswordResetCode: (actionCode: string) => Promise<string>;
}

export interface AuthContextInterface {
  auth: AuthInstanceInterface;
  logOut: () => void;
}

export const AuthContext = createContext<AuthContextInterface | undefined>(undefined);
